import { useEffect, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Center, Loader } from '@mantine/core';
import { useAuth } from './useAuth';

interface Props {
  children: ReactNode;
}

/**
 * Route guard for authenticated screens. While the `/me` probe is in flight it
 * shows a centered loader; if it resolves to "no session" it redirects to
 * /login; once a user is present it renders its children.
 *
 * Wrap a protected route's component with this once feature routes (items,
 * orders, picks) land — e.g. `component: () => <RequireAuth><Items /></RequireAuth>`.
 * The global 401 interceptor (see shared/api/client + main.tsx) covers sessions
 * that expire mid-use; this covers arriving at a protected URL unauthenticated.
 */
export function RequireAuth({ children }: Props) {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      void navigate({ to: '/login' });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <Center mih="100vh">
        <Loader color="invetrak" />
      </Center>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
